import { sendToApi } from 'api/client'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { AlmanacFile, MediaTransformations } from 'types'
import { xhrUpload } from './xhrUpload'

export interface BaseCreateMediaFileParams {
  source_url: string
  mime_type: string
  title: string
  project_key: string
  variants?: Array<MediaTransformations>
  show_in_file_system?: boolean
  extract_metadata_in_background_job?: boolean
}

interface CreateBrandKitMediaFileParams extends BaseCreateMediaFileParams {
  brand_kit_id: number
  campaign_id?: never
}

interface CreateCampaignFileParams extends BaseCreateMediaFileParams {
  brand_kit_id?: never
  campaign_id: number
}

export type CreateMediaFileParams = BaseCreateMediaFileParams | CreateBrandKitMediaFileParams | CreateCampaignFileParams

export const MediaFileClient = {
  create: (workspaceId: number, body: CreateMediaFileParams) => {
    return sendToApi(`/w/${workspaceId}/media_files`, {
      method: 'POST',
      body,
    })
  },
  getAlmanacFile: (key: string) => {
    return sendToApi<{ almanac_file: AlmanacFile }>(`/almanac_files/${key}/show_maximal`, {
      method: 'GET',
    })
  },
  update: (workspaceId: number, id: number, media_file: AlmanacFile) => {
    return sendToApi(`/w/${workspaceId}/media_files/${id}`, {
      method: 'PUT',
      body: {
        media_file,
      },
    })
  },
  destroy: (workspaceId: number, id: number) => {
    return sendToApi(`/w/${workspaceId}/media_files/${id}`, {
      method: 'DELETE',
    })
  },
  clearMediaUsage: (workspaceId: number, brandKitId: number, mediaFileIds: number[]) => {
    return sendToApi(`/w/${workspaceId}/brand_kits/${brandKitId}/clear_media_usage`, {
      method: 'POST',
      body: {
        media_file_ids: mediaFileIds,
      },
    })
  },
  getSignedUrl: (workspaceId: number, project_key: string, filename: string) => {
    return sendToApi<{ pre_signed_url: string }>(`/w/${workspaceId}/media_files/pre_signed_url`, {
      method: 'POST',
      body: {
        filename,
        project_key,
      },
    })
  },
  uploadToS3: (
    preSignedUrl: string | (() => Promise<string>),
    file: File,
    onProgress?: (progress: number) => void,
    workspaceId?: number,
  ): Promise<{ url: string }> =>
    xhrUpload({
      url: preSignedUrl,
      body: file,
      contentType: file.type,
      onProgress,
      onRetry: () =>
        showToastMessage(
          "Upload interrupted. We're automatically trying your upload again. No need to do anything!",
          'info',
        ),
      errorTags: {
        workspace_id: workspaceId,
        file_size_mb: Math.round(file.size / (1024 * 1024)),
        mime_type: file.type,
      },
    }),
}
